import { useEffect, useState } from 'react';
import { useGetCurrentUserSettings } from '../services/react-query/users/useGetCurrentUserSettings';
import { useUpdateUserSorts } from '../services/react-query/users/useUpdateUserSorts';
import { SortSettings, UserConfigurableModule } from '../types/User';
import { ITableConfigurableModules } from '../types/Itable';

export type AbstractSortParam = {
  [key: string]: 1 | -1;
};

export const useUserSorts = <K extends AbstractSortParam>(module: ITableConfigurableModules) => {
  const { data: userSettingsData } = useGetCurrentUserSettings();
  const { mutate: updateUserSorts } = useUpdateUserSorts();

  const [userSorts, setUserSorts] = useState<K>({} as K);

  // Initialize user sorts from user settings
  useEffect(() => {
    const sorts: SortSettings = userSettingsData?.settings?.sorts || {};
    const moduleSorts = (sorts[module] || {}) as K;
    setUserSorts(moduleSorts);
  }, [userSettingsData, module]);

  /**
   * Toggles sorting for a column, ensuring only one column is sorted at a time:
   * - If the current column is `1`, set to `-1`
   * - If the current column is `-1`, remove sorting
   * - If another column is sorted, reset sorting and apply new sort to `1`
   *
   * @param key - The column key to sort.
   */
  const onSort = (key: keyof K) => {
    const currentKey = Object.keys(userSorts)[0] as keyof K | undefined; // Get current sorted column (if any)
    const updatedSorts: Partial<K> = {};

    if (currentKey === key) {
      // Toggle between `1`, `-1`, and removing sort
      if (userSorts[key] === 1) {
        updatedSorts[key] = -1 as K[keyof K];
      } else {
        delete updatedSorts[key]; // Remove sort if `-1`
      }
    } else {
      // Clear previous sort key and apply new sorting
      updatedSorts[key] = 1 as K[keyof K];
    }

    const payload = {
      sorts: {
        ...(userSettingsData?.settings?.sorts || {}),
        [module]: updatedSorts,
      },
    };

    updateUserSorts(payload);
    setUserSorts(updatedSorts as K);
  };

  return { userSorts, onSort };
};
